import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';

interface StationForm {
  name: string;
  address: string;
  capacity: string;
}

interface CreateGroupStationDialogProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateGroupStationDialog({
  groupId,
  open,
  onOpenChange,
}: CreateGroupStationDialogProps) {
  const { t } = useTranslation();
  const create = hooks.useCreateGroupStation();
  const { register, handleSubmit, formState, reset } = useForm<StationForm>({
    defaultValues: { name: '', address: '', capacity: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const name = values.name.trim();
    if (!name) {
      toast.error(t('station.create.nameRequired'));
      return;
    }
    const capacity = values.capacity.trim() ? Number(values.capacity) : undefined;
    try {
      await create.mutateAsync({
        groupId,
        name,
        address: values.address.trim() || undefined,
        type: 'public',
        status: 'active',
        capacity: Number.isFinite(capacity) ? capacity : undefined,
        displayOnMap: true,
      });
      toast.success(t('group.station.created'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('group.station.createTitle')}</DialogTitle>
          <DialogDescription>{t('group.station.createDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="station-name">{t('station.name')}</Label>
            <Input id="station-name" {...register('name', { required: true })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="station-address">{t('station.address')}</Label>
            <Input id="station-address" {...register('address')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="station-capacity">{t('group.station.capacity')}</Label>
            <Input id="station-capacity" type="number" min={1} {...register('capacity')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || create.isPending}>
              {t('actions.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
